"use client";

import { c as _c } from "react/compiler-runtime";
import { extractErrorCode } from "@local/error/src/extractErrorCode";
import { capturePostHogException } from "@local/tracking/src/scripts/posthog";
import { useEffect, useTransition } from "react";
interface ErrorPageProps {
  error: Error & {
    digest?: string;
    code?: string;
  };
  /**
   * Next's `retry`, not `reset`. `reset()` only clears boundary state and
   * re-renders, so it cannot recover a Server Component error — which is what
   * nearly every error reaching this page is (an Offer API failure). `retry()`
   * re-fetches the segment. Stable since Next 16.3.
   */
  retry: () => void;
  heading?: string;
}
export const ErrorPage = t0 => {
  const $ = _c(28);
  const {
    error,
    retry,
    heading: t1
  } = t0;
  const heading = t1 === undefined ? "Something went wrong" : t1;
  const [isRetrying, startRetry] = useTransition();
  let t2;
  if ($[0] !== error) {
    t2 = extractErrorCode(error) ?? "STORE-X001";
    $[0] = error;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const errorCode = t2;
  let t3;
  if ($[2] !== error.digest) {
    t3 = error.digest?.slice(0, 8).toUpperCase();
    $[2] = error.digest;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const refCode = t3;
  let t4;
  let t5;
  if ($[4] !== error || $[5] !== errorCode) {
    t4 = () => {
      if (error.digest) {
        return;
      }
      capturePostHogException(error, {
        error_code: errorCode,
        boundary: "error-page"
      });
    };
    t5 = [error, errorCode];
    $[4] = error;
    $[5] = errorCode;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== heading) {
    t6 = <h5 className="text-h5 leading-heading mb-4 font-semibold">{heading}</h5>;
    $[8] = heading;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <p className="mb-2 text-gray-600">We encountered an unexpected error. Please try again.</p>;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== errorCode) {
    t8 = <p>Error: {errorCode}</p>;
    $[11] = errorCode;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== refCode) {
    t9 = refCode && <p>Reference: {refCode}</p>;
    $[13] = refCode;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8 || $[16] !== t9) {
    t10 = <div className="mb-6 space-y-1 font-mono text-xs text-gray-400">{t8}{t9}</div>;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== retry) {
    t11 = () => startRetry(() => retry());
    $[18] = retry;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = isRetrying ? "Retrying\u2026" : "Try again";
  let t13;
  if ($[20] !== isRetrying || $[21] !== t11 || $[22] !== t12) {
    t13 = <button type="button" onClick={t11} disabled={isRetrying} aria-label="Try loading the page again" className="min-h-11 rounded-full bg-gray-900 px-6 py-2 text-white transition-colors hover:bg-gray-700 focus-visible:ring-2 focus-visible:ring-gray-700 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-[50px]">{t12}</button>;
    $[20] = isRetrying;
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t10 || $[25] !== t13 || $[26] !== t6) {
    t14 = <div role="alert" id="error-page" className="flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">{t6}{t7}{t10}{t13}</div>;
    $[24] = t10;
    $[25] = t13;
    $[26] = t6;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  return t14;
};